import { IonButton, IonIcon } from '@ionic/react';
import { notifications, notificationsOffOutline } from 'ionicons/icons';
import { ReactElement, useCallback } from 'react';
import {
  EventChannelObject,
  EventChannelType,
  useCreateEventSubscriptionMutation,
  useListEventChannelsQuery,
  useListEventDefinitionsQuery,
  useRemoveEventSubscriptionMutation,
} from '../../api/graphql/generated';
import { useGetChatSubscription } from '../../api/hooks/chat.hooks';
import { SaveStatusToast } from '../../components/toast/toast.gql';
import { trackChatNotificationSubscription } from '../../config/analytics';

interface ChatNotificationToggleButtonProps {
  chatId: string;
}

const ChatNotificationToggleButton = (
  props: ChatNotificationToggleButtonProps
): ReactElement => {
  const chatChannelLabel = 'Notify Me';
  const chatDefinitionName = 'chat_notify_me_on_everything';

  const { chatId } = props;
  const { data: listEventChannelsData } = useListEventChannelsQuery({
    variables: {
      input: {
        object: EventChannelObject.CHAT,
      },
    },
  });

  const chatChannel = listEventChannelsData?.listEventChannels.items?.find(
    (channel) =>
      channel.type === EventChannelType.USER &&
      channel.label === chatChannelLabel
  );

  const { data: chatEventDefinitionData } = useListEventDefinitionsQuery({
    variables: {
      input: {
        parentId: chatId,
      },
    },
  });

  const chatEventDefinition =
    chatEventDefinitionData?.listEventDefinitions.items?.find(
      (defintion) => defintion.name === chatDefinitionName
    );

  const [
    createEventSubscription,
    { error: createError, loading: createLoading },
  ] = useCreateEventSubscriptionMutation();

  const [
    removeEventSubscription,
    { error: removeError, loading: removeLoading },
  ] = useRemoveEventSubscriptionMutation();

  const [existingSubscription, refetchEventSubscriptions] =
    useGetChatSubscription(chatId);

  const isSubscribedToChatNotifications = existingSubscription !== undefined;

  const toggleNotifications = useCallback(async () => {
    if (isSubscribedToChatNotifications) {
      await removeEventSubscription({
        variables: {
          input: {
            id: existingSubscription.id,
          },
        },
      });
    } else if (chatChannel !== undefined && chatEventDefinition !== undefined) {
      await createEventSubscription({
        variables: {
          input: {
            eventChannelId: chatChannel?.id,
            eventDefinitionId: chatEventDefinition?.id,
          },
        },
      });
    }
    trackChatNotificationSubscription(isSubscribedToChatNotifications);
    refetchEventSubscriptions();
  }, [
    isSubscribedToChatNotifications,
    existingSubscription,
    removeEventSubscription,
    createEventSubscription,
    chatChannel,
    chatEventDefinition,
    refetchEventSubscriptions,
  ]);

  const icon = isSubscribedToChatNotifications
    ? notifications
    : notificationsOffOutline;

  return (
    <>
      <IonButton
        onClick={() => {
          toggleNotifications();
        }}
      >
        <IonIcon icon={icon} />
      </IonButton>
      <SaveStatusToast loading={createLoading} error={createError} />
      <SaveStatusToast loading={removeLoading} error={removeError} />
    </>
  );
};

export { ChatNotificationToggleButton };
